import { openDB, DBSchema } from 'idb';
import { v4 as uuidv4 } from 'uuid';
import { Product } from './types';
import toast from 'react-hot-toast';

interface LotisModeDB extends DBSchema {
  products: {
    key: number;
    value: Product;
    indexes: {
      'by-status': string;
      'by-client': string;
    };
  };
  sync_sessions: {
    key: string;
    value: {
      id: string;
      started_at: string;
      completed_at?: string;
      status: string;
      total_products: number;
      synced_products: number;
      failed_products: number;
    };
  };
}

// Ouvrir la base de données
export const getLocalDB = async () => {
  return openDB<LotisModeDB>('lotismode-local', 1, {
    upgrade(db) {
      // Créer les tables si elles n'existent pas
      if (!db.objectStoreNames.contains('products')) {
        const productStore = db.createObjectStore('products', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        productStore.createIndex('by-status', 'status');
        productStore.createIndex('by-client', 'client_number');
      }

      if (!db.objectStoreNames.contains('sync_sessions')) {
        db.createObjectStore('sync_sessions', { keyPath: 'id' });
      }

      console.log('Base de données locale initialisée');
    },
  });
};

// Fonctions pour manipuler les produits en local
export const getLocalProducts = async (status: string): Promise<Product[]> => {
  try {
    const db = await getLocalDB();
    return await db.getAllFromIndex('products', 'by-status', status);
  } catch (error) {
    console.error('Erreur lors de la récupération des produits locaux:', error);
    return [];
  }
};

export const addLocalProduct = async (product: Omit<Product, 'id'>): Promise<number> => {
  try {
    const db = await getLocalDB();
    const id = await db.add('products', {
      ...product,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as Product);
    
    return id as number;
  } catch (error) {
    console.error('Erreur lors de l\'ajout d\'un produit local:', error);
    throw error;
  }
};

export const updateLocalProduct = async (id: number, updates: Partial<Product>): Promise<boolean> => {
  try {
    const db = await getLocalDB();
    const product = await db.get('products', id);
    
    if (!product) {
      throw new Error('Produit non trouvé');
    }
    
    const updatedProduct = {
      ...product,
      ...updates,
      updated_at: new Date().toISOString(),
    };
    
    await db.put('products', updatedProduct);
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour d\'un produit local:', error);
    return false;
  }
};

export const deleteLocalProduct = async (id: number): Promise<boolean> => {
  try {
    const db = await getLocalDB();
    await db.delete('products', id);
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression d\'un produit local:', error);
    return false;
  }
};

// Mode hors-ligne : fonctions de gestion des sessions de synchronisation
export const createLocalSyncSession = async (products: Product[]): Promise<string> => {
  try {
    const syncId = uuidv4();
    const db = await getLocalDB();
    
    await db.add('sync_sessions', {
      id: syncId,
      started_at: new Date().toISOString(),
      status: 'in_progress',
      total_products: products.length,
      synced_products: 0,
      failed_products: 0
    });
    
    return syncId;
  } catch (error) {
    console.error('Erreur lors de la création d\'une session de synchronisation locale:', error);
    throw error;
  }
};

export const updateLocalSyncSession = async (
  syncId: string, 
  updates: Partial<{
    synced_products: number;
    failed_products: number;
    status: string;
    completed_at: string;
  }>
): Promise<boolean> => {
  try {
    const db = await getLocalDB();
    const session = await db.get('sync_sessions', syncId);
    
    if (!session) {
      throw new Error('Session non trouvée');
    }
    
    const updatedSession = {
      ...session,
      ...updates,
    };
    
    await db.put('sync_sessions', updatedSession);
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour d\'une session de synchronisation locale:', error);
    return false;
  }
};

// Initialisation du mode démo avec des données de test
export const initDemoMode = async (): Promise<void> => {
  try {
    console.log('Initialisation du mode démo...');
    
    // Vérifier si des données existent déjà
    const productsCount = (await getLocalProducts('draft')).length;
    if (productsCount > 0) {
      console.log('Des données existent déjà en mode démo');
      return;
    }
    
    // Créer quelques produits de démonstration
    await addLocalProduct({
      client_number: '1001',
      items: JSON.stringify(['Haut 25€', 'Jupe 35€']),
      total_price: 60,
      status: 'draft',
    });
    
    await addLocalProduct({
      client_number: '1002',
      items: JSON.stringify(['Robe 50€', 'Accessoire 15€']),
      total_price: 65,
      status: 'draft',
    });
    
    await addLocalProduct({
      client_number: '1003',
      items: JSON.stringify(['Jeans 45€']),
      total_price: 45,
      status: 'pending',
    });
    
    await addLocalProduct({
      client_number: '1004',
      items: JSON.stringify(['ENS 70€']),
      total_price: 70,
      status: 'completed',
    });
    
    console.log('Mode démo initialisé avec succès');
    toast.success('Mode hors-ligne activé avec des données de démonstration');
  } catch (error) {
    console.error('Erreur lors de l\'initialisation du mode démo:', error);
  }
};